import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltCard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(mx, { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
      style={{ perspective: 1100 }}
      className="relative"
    >
      <div className="absolute -inset-10 rounded-full bg-[#7650c7]/25 blur-3xl" aria-hidden />
      <motion.div
        ref={ref}
        data-testid="hero-tilt-card"
        onMouseMove={onMove}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-full max-w-[420px]"
      >
        <div
          className="absolute inset-0 translate-x-5 translate-y-6 rotate-3 rounded-[1.75rem] bg-[#f4d35e]"
          style={{ transform: "translateZ(-50px) rotate(4deg)" }}
          aria-hidden
        />
        <div className="relative overflow-hidden rounded-[1.75rem] bg-[#f3f0e8] p-7 shadow-2xl shadow-black/40 sm:p-9">
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-[#6d687c]">
            <span>elavate / live preview</span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7abcb0]" />
              online
            </span>
          </div>
          <p className="mt-10 font-display text-5xl font-bold leading-[0.95] tracking-[-0.05em] text-[#201e32]">
            Made
            <br />
            for you<span className="text-[#7650c7]">.</span>
          </p>
          <p className="mt-5 max-w-[260px] text-sm leading-relaxed text-[#6d687c]">
            Not a theme everyone has already seen — a visual direction made around you.
          </p>
          <div className="mt-9 flex items-center gap-3">
            <span className="rounded-full bg-[#7650c7] px-4 py-2 text-xs font-semibold text-[#f3f0e8]">
              Quote by quote
            </span>
            <span className="rounded-full border border-[#201e32]/15 px-4 py-2 text-xs font-semibold text-[#201e32]/70">
              Since 2018
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { scrollYProgress } = useScroll();
  const ringY = useTransform(scrollYProgress, [0, 0.25], [0, -110]);
  const glowY = useTransform(scrollYProgress, [0, 0.25], [0, -60]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#201e32] px-5 pb-24 pt-32 text-[#f3f0e8] sm:px-8 sm:pt-40 lg:px-10"
    >
      <motion.div
        style={{ y: ringY }}
        aria-hidden
        className="absolute -right-24 top-16 h-[540px] w-[540px] rounded-full border border-white/10 sm:right-0"
      />
      <motion.div
        style={{ y: glowY }}
        aria-hidden
        className="absolute -right-4 top-44 h-[380px] w-[380px] rounded-full border border-[#9b72f2]/25"
      />
      <div aria-hidden className="absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[#7650c7]/15 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#a277fa]"
          >
            Since 2018 · Based in the UK · working everywhere
          </motion.p>

          <h1 className="mt-8 font-display text-[clamp(3.1rem,7.6vw,6.8rem)] font-bold leading-[0.93] tracking-[-0.05em]">
            <MaskedLine delay={0.25}>A website that</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-[#a277fa]">pulls its weight.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
            className="mt-8 max-w-[510px] text-base leading-relaxed text-white/65 sm:text-lg"
          >
            Bespoke websites for small businesses and people with something worth putting
            online. Clear thinking, careful craft, no intimidating agency theatre.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-start"
              onClick={() => scrollToSection("#contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#f4d35e] px-6 py-3.5 text-sm font-semibold text-[#201e32] transition-transform duration-300 hover:-translate-y-1"
            >
              Start a conversation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-process"
              onClick={() => scrollToSection("#process")}
              className="group inline-flex items-center gap-3 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-[#f3f0e8] transition-colors duration-300 hover:border-[#a277fa] hover:text-[#a277fa]"
            >
              See how we work
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/40"
          >
            <span>7-day builds</span>
            <span>Quote by quote</span>
            <span>No jargon</span>
          </motion.div>
        </div>

        <div className="hidden justify-end lg:col-span-5 lg:flex">
          <TiltCard />
        </div>
      </div>
    </section>
  );
};
